import {useMemo} from 'react'
import {getColor, haveActionTypes, remapNotificationType, shouldGoTo} from '../../utilities/utilities'
import NotificationIcon from '../notificationIcon/NotificationIcon'
import {NotificationsType} from '../../types'
import {
    StyledDate,
    StyledDescriptionContainer,
    StyledIconBox,
    StyledNotificationCard,
    StyledNotificationTitle
} from './style'
import {useTranslation} from 'react-i18next'
import dayjs, {formatDate} from '@/dayjs'
import {NotificationsTypesE} from '../../services/notifications.schema'
import {useNavigate} from 'react-router-dom'
import {
    ROUTE_EDIT_PROFILE,
    ROUTE_HOME,
    ROUTE_HOST_PAYMENT_METHOD,
    ROUTE_TELEGRAM_BOT
} from '@/utilities/constants/routeNames'
import {useMe} from '@/features/authentication/queries/useMe'
import {UserV3} from '@/features/authentication/types'
import {useTheme} from 'styled-components'
import {useQueryClient} from '@tanstack/react-query'
import {QUERY_KEYS} from '@/features/moengage/queries/keys'
import {generateChatChannelName} from '@/utilities/helpers'
import {useGetGoalsForCreator} from '@/features/goal/queries/useGetGoalsForCreator'
import {useFetchGoalForOwnerV3} from '@/features/goal/queries/useFetchGoalForOwnerV3'
import {isArray} from 'lodash'

type NewNotification = {
    notification: NotificationsType
    old?: boolean
    closeNotification: () => void
}

export const NotificationToast = ({notification, old, closeNotification}: NewNotification) => {
    const {data} = useMe()
    const theme = useTheme()
    const user = data as UserV3
    const {t} = useTranslation()
    const navigate = useNavigate()
    const hasGoalId = !!notification?.goalId
    const {data: goal} = useFetchGoalForOwnerV3(hasGoalId ? `${notification.goalId}` : null)
    const {data: goalsList} = useGetGoalsForCreator()

    const typeRemapped = useMemo(() => remapNotificationType(notification.type), [notification.type])
    const type = notification.type || notification.notificationType.notificationType
    const color = getColor(typeRemapped, theme)
    const queryClient = useQueryClient()

    const redirectToPath = useMemo(() => {
        switch (type) {
            case NotificationsTypesE.HOST_REJECTED_PAYMENT_ID_CARD:
            case NotificationsTypesE.ID_CARD_REJECTED:
            case NotificationsTypesE.BACK_ID_CARD_REJECTED:
            case NotificationsTypesE.FRONT_ID_CARD_REJECTED:
            case NotificationsTypesE.SELFIE_ID_CARD_REJECTED:
                return () => {
                    navigate(ROUTE_EDIT_PROFILE, {state: {fromProfileError: true}})
                    closeNotification()
                }
            case NotificationsTypesE.HOST_REJECTED_PAYMENT_NO_METHOD:
                return () => {
                    navigate(ROUTE_HOST_PAYMENT_METHOD)
                    closeNotification()
                }
            case NotificationsTypesE.HOST_CHANGE_GIFT_AMOUNT:
            case NotificationsTypesE.TELEGRAM_BOT:
                return () => {
                    navigate(ROUTE_TELEGRAM_BOT)
                    closeNotification()
                }
            case NotificationsTypesE.GOT_BONUS_COUPON:
            case NotificationsTypesE.GOT_REFUNDED_GIFT_COUPON:
                return
            case NotificationsTypesE.HOST_LOGIN:
                return () => {
                    navigate(ROUTE_HOME)
                    closeNotification()
                }
            default:
                return
        }
    }, [type])

    const invalidateMoengage = () => {
        queryClient.invalidateQueries({queryKey: [QUERY_KEYS.moengage_campaign]})
    }

    const channelId =
        user && notification?.refUserId && generateChatChannelName(user.id, user.type, notification?.refUserId)

    const path = shouldGoTo({
        type,
        typeRemapped,
        notification,
        user,
        goalsList,
        goal,
        invalidateMoengage,
        channelId: channelId || null
    })

    const onClickAction = () => {
        if (path) {
            closeNotification()
            navigate(path)
        } else {
            redirectToPath?.()
        }
    }

    let haveAction = false

    if (path || haveActionTypes.includes(type)) {
        haveAction = true
    }

    const notificationDate = useMemo(() => {
        const date = dayjs(notification.createdAt)
        if (date.format('DD') === dayjs().format('DD')) {
            return date.format('H:mm')
        } else if (date.format('D') === (parseInt(dayjs().format('D')) - 1).toString()) {
            return `${t('common:yesterday')} ${date.format('H:mm')}`
        } else {
            return date.format('DD MMM, H:mm')
        }
    }, [notification.createdAt])
    return (
        <StyledNotificationCard haveAction={haveAction} color={color} old={old} onClick={onClickAction}>
            <StyledDescriptionContainer>
                <StyledIconBox>
                    <NotificationIcon old={old} color={color} type={typeRemapped} />
                </StyledIconBox>
                <div>
                    <StyledNotificationTitle>
                        {notification.type === NotificationsTypesE.MOENGAGE_NOTIFICATION
                            ? t('common:notification_moengage_title')
                            : notification?.notificationType?.title}
                    </StyledNotificationTitle>
                    {notification?.content && isArray(notification.content)
                        ? notification.content.map(content => (Date.parse(content) ? formatDate(content) : content))
                        : notification?.content}
                </div>
            </StyledDescriptionContainer>
            <StyledDate>{notification.createdAt && <div>{notificationDate}</div>}</StyledDate>
        </StyledNotificationCard>
    )
}
